// ============================================================
// fondos/efectos/comun.js — lo que comparten los efectos de LIENZO (módulo PURO)
//
// P3 del motor de fondos, fase F2b (`CLAUDE_RESUMEN_PLANES/2026-09-24_plan-p3-motor-efectos.md`
// §2.2): los 9 lienzos dejan de ser clases de `vendor/bg/` —cada una con su bucle, su throttle, su
// `resize` y su copia de `_hexToRgb`— y pasan a EFECTOS: un objeto con `crear`, `redimensionar`,
// `dibujar` y (opcional) `destruir`, que pinta en el contexto que le da `fondos/lienzo.js` y avanza
// con el tiempo que le da el motor.
//
// MÓDULO PURO: ES2019 sin `?.` ni `??`, sin DOM y sin estado de módulo (el test de contrato los
// carga en node 12 con un contexto 2D falso).
// ============================================================

/**
 * El PASO de siempre (ms): las clases de `vendor/bg/` movían sus partículas UN paso por fotograma
 * pintado, con un throttle de 33 ms (30 fps). Esa es la velocidad que se ve hoy en una pantalla de
 * 60, 90 o 120 Hz, y la que conserva el efecto portado: cada `dt` avanza `dt / PASO` pasos. Es una
 * constante de CALIBRACIÓN, no la cadencia: si el motor pintara a 60 fps, los fondos irían a la
 * misma velocidad, más suaves.
 */
export const PASO = 1000 / 30;

/**
 * Tamaño relativo de lo que pinta un lienzo sobre una sección CLARA (E13, F3 del P3). Un punto o un
 * trazo oscuro sobre claro parece más pequeño que el mismo punto claro sobre oscuro (irradiación), y
 * neural, particles y constellation estaban pensados para oscuro: sobre claro casi no se veían. En
 * oscuro todo queda como siempre (factor 1).
 */
export const TAM_CLARO = 1.3;

/** El factor de tamaño de una polaridad (ver `TAM_CLARO`). */
export function tamano(oscuro) {
    return oscuro ? 1 : TAM_CLARO;
}

/** Cuántos pasos de los de siempre caben en `dt` ms (0 en un fotograma quieto). */
export function pasos(dt) {
    return dt > 0 ? dt / PASO : 0;
}

/**
 * '#rrggbb' o '#rgb' → {r, g, b}, EXACTAMENTE como las clases de la primera generación de
 * `vendor/bg/` (neural, particles, constellation, geometric, bokeh, matrix): un color que no es hex
 * da negro. No se "arregla": es lo que pinta hoy un acento guardado en otro formato.
 */
export function rgbDe(hex) {
    let h = hex.replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const num = parseInt(h, 16);
    return { r: num >> 16, g: (num >> 8) & 255, b: num & 255 };
}

/**
 * Lo mismo, como la segunda generación (la clase base `vendor/bg/_base.js`: waves, dot-grid y
 * topographic): sin color, o con uno que no es hex, el índigo de siempre.
 */
export function rgbDeBase(hex) {
    let h = (hex || '#6366f1').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    const n = parseInt(h, 16);
    if (isNaN(n)) return { r: 99, g: 102, b: 241 };
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

/**
 * `{r, g, b}` + alfa → 'rgba(r, g, b, a)', con el alfa a 4 decimales (nunca en notación exponencial) y
 * redondeado HACIA ABAJO: la parada de un degradado que vale el tope no puede salir por encima de él.
 */
export function rgba(c, a) {
    return `rgba(${c.r}, ${c.g}, ${c.b}, ${Math.floor(a * 10000 + 1e-7) / 10000})`;
}

/**
 * Un parámetro 1-10 del catálogo interpolado entre `base` y `max` (densidad → número de elementos),
 * con el defecto PROPIO del efecto cuando el dato no lo trae (`!= null`, como siempre: ausente y
 * null son lo mismo). Sin redondear: cada efecto redondea como lo hacía.
 */
export function interpolar(valor, defecto, base, max) {
    const v = valor != null ? valor : defecto;
    return base + (v - 1) * (max - base) / 9;
}
